// CSV parsing utilities for product upload

export interface ValidationError {
  group: string;
  field: string;
  message: string;
}

export interface ParsedProduct {
  title: string;
  description: string;
  brand_owner: string;
  brand_name: string;
  subbrand_name?: string;
  ingredients: string;
  serving_size: number;
  serving_size_unit: string;
  household_serving_fulltext: string;
  branded_food_category?: string;
  data_source?: string;
  gtin_upc?: string;
  package_weight?: string;
  market_country?: string;
  images: { image: string }[];
  nutrients: { nutrient_id: number; amount: number; nutrient_title?: string }[];
}

export interface UploadResult {
  fdc_id: number;
  title: string;
  image: string | null;
}

/**
 * Splits a single CSV line into fields, correctly handling double-quoted fields
 * that may contain commas (e.g. "Water, Sugar, Natural Flavors").
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export function parseProductCSV(text: string): {
  products: ParsedProduct[];
  errors: ValidationError[];
} {
  const lines = text.split('\n').map((l) => l.replace(/\r$/, '')).filter((l) => l.trim() !== '');

  if (lines.length < 2) {
    return {
      products: [],
      errors: [{ group: '-', field: 'file', message: 'CSV must have a header row and at least one data row' }],
    };
  }

  const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase());

  const requiredHeaders = ['group', 'title', 'brand_owner', 'brand_name', 'serving_size', 'serving_size_unit'];
  const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));
  if (missingHeaders.length > 0) {
    return {
      products: [],
      errors: [{ group: '-', field: 'header', message: `Missing required columns: ${missingHeaders.join(', ')}` }],
    };
  }

  const col = (row: string[], name: string) => row[headers.indexOf(name)] ?? '';

  const groups = new Map<string, string[][]>();
  for (let i = 1; i < lines.length; i++) {
    const row = splitCsvLine(lines[i]);
    const key = col(row, 'group').trim();
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const products: ParsedProduct[] = [];
  const errors: ValidationError[] = [];

  for (const [key, rows] of groups) {
    const firstRow = rows[0];

    const title = col(firstRow, 'title').trim();
    const description = col(firstRow, 'description').trim();
    const brand_owner = col(firstRow, 'brand_owner').trim();
    const brand_name = col(firstRow, 'brand_name').trim();
    const subbrand_name = col(firstRow, 'subbrand_name').trim() || undefined;
    const ingredients = col(firstRow, 'ingredients').trim();
    const servingSizeRaw = col(firstRow, 'serving_size').trim();
    const serving_size_unit = col(firstRow, 'serving_size_unit').trim();
    const household_serving_fulltext = col(firstRow, 'household_serving_fulltext').trim();
    const branded_food_category = col(firstRow, 'branded_food_category').trim() || undefined;
    const data_source = col(firstRow, 'data_source').trim() || undefined;
    const gtin_upc = col(firstRow, 'gtin_upc').trim() || undefined;
    const package_weight = col(firstRow, 'package_weight').trim() || undefined;
    const market_country = col(firstRow, 'market_country').trim() || undefined;

    if (!title) errors.push({ group: key, field: 'title', message: 'title is required' });
    if (!brand_owner) errors.push({ group: key, field: 'brand_owner', message: 'brand_owner is required' });
    if (!brand_name) errors.push({ group: key, field: 'brand_name', message: 'brand_name is required' });
    if (!ingredients) errors.push({ group: key, field: 'ingredients', message: 'ingredients is required' });
    if (!serving_size_unit) errors.push({ group: key, field: 'serving_size_unit', message: 'serving_size_unit is required' });
    if (!household_serving_fulltext) errors.push({ group: key, field: 'household_serving_fulltext', message: 'household_serving_fulltext is required' });

    const serving_size = parseFloat(servingSizeRaw);
    if (!servingSizeRaw || !isFinite(serving_size) || serving_size <= 0) {
      errors.push({ group: key, field: 'serving_size', message: 'serving_size must be a positive number' });
    }

    const images: { image: string }[] = [];
    const nutrients: { nutrient_id: number; amount: number; nutrient_title?: string }[] = [];

    for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
      const row = rows[rowIdx];
      const imageVal = headers.includes('image') ? col(row, 'image').trim() : '';
      const nutrientIdRaw = headers.includes('nutrient_id') ? col(row, 'nutrient_id').trim() : '';
      const amountRaw = headers.includes('amount') ? col(row, 'amount').trim() : '';
      const nutrientTitle = headers.includes('nutrient_title') ? col(row, 'nutrient_title').trim() : '';

      if (imageVal) {
        images.push({ image: imageVal });
      }

      const hasNutrientId = nutrientIdRaw !== '';
      const hasAmount = amountRaw !== '';

      if (hasNutrientId || hasAmount) {
        const nutrient_id = parseInt(nutrientIdRaw, 10);
        const amount = parseFloat(amountRaw);

        if (!hasNutrientId || !Number.isInteger(nutrient_id) || nutrient_id <= 0) {
          errors.push({ group: key, field: 'nutrient_id', message: `Row ${rowIdx + 1}: nutrient_id must be a positive integer` });
        } else if (!hasAmount || !isFinite(amount)) {
          errors.push({ group: key, field: 'amount', message: `Row ${rowIdx + 1}: amount must be a finite number` });
        } else {
          nutrients.push({ nutrient_id, amount, nutrient_title: nutrientTitle || undefined });
        }
      }
    }

    if (errors.filter((e) => e.group === key).length === 0) {
      products.push({
        title, description, brand_owner, brand_name, subbrand_name,
        ingredients, serving_size, serving_size_unit, household_serving_fulltext,
        branded_food_category, data_source, gtin_upc, package_weight, market_country,
        images, nutrients,
      });
    }
  }

  return { products, errors };
}
